import { StyleSheet, Platform } from 'react-native';
import { FontFamily } from '../../constants/fonts';

// ─── Type-badge styles ────────────────────────────────────────────────────────

export const tbadge = StyleSheet.create({
  // matches design .tag class
  wrap: {
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 3,
    alignSelf: 'flex-start',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: '100%',
    ...Platform.select({
      web: {
        wordWrap: 'break-word' as any,
        overflowWrap: 'break-word' as any,
      },
    }),
  },
  text: { fontSize: 11, fontWeight: '700', fontFamily: FontFamily.bold, flexWrap: 'wrap' },
  // tag.blue  → bg:#DAEEFA color:#01346F
  blue: { backgroundColor: '#DAEEFA' },
  // tag.orange → bg:#F9E0CA color:#4D2E0C  (design --nav-orange-tagbg)
  orange: { backgroundColor: '#F9E0CA' },
  blueText: { color: '#01346F' },
  orangeText: { color: '#4D2E0C' },
});

// ─── Main styles ──────────────────────────────────────────────────────────────

export const styles = StyleSheet.create({
  // Page container — design: content padding 24px 32px 64px
  root: { flex: 1, backgroundColor: '#F5F9FF' },
  scrollContent: { padding: 24, paddingBottom: 64, gap: 20,
    ...Platform.select({ web: { paddingHorizontal: 32 } } as any),
  },

  // Read-only banner — design: .read-note
  readNote: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    backgroundColor: '#DAEEFA', borderWidth: 1, borderColor: '#C6DCFB',
    borderRadius: 8, padding: 10, paddingHorizontal: 14,
  },
  readNoteText: { fontSize: 13, fontWeight: '500', color: '#01346F', fontFamily: FontFamily.medium, flex: 1 },

  // Card — design: .card  shadow: var(--nav-elev-01) = 0px 0px 24px rgba(0,94,192,.08)
  card: {
    backgroundColor: '#fff', borderRadius: 8, padding: 24,
    ...Platform.select({
      web: { overflow: 'visible' } as any,
      ios: { shadowColor: 'rgba(0,94,192,1)', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.08, shadowRadius: 24 },
      android: { elevation: 2 },
    }),
  },
  // Stop Issue card — zIndex:3 keeps its stacking context above the Route Issue
  // card (zIndex:2) below it, so the customer typeahead floats on top.
  cardStopIssue: {
    backgroundColor: '#fff', borderRadius: 8, padding: 24,
    ...Platform.select({
      web: { overflow: 'visible', zIndex: 3 } as any,
      ios: { shadowColor: 'rgba(0,94,192,1)', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.08, shadowRadius: 24 },
      android: { elevation: 2 },
    }),
  },
  // Route Issue card — sits above the table card sibling so the
  // HarborDropdown menu is not obscured by the table rendered below.
  cardRouteIssue: {
    backgroundColor: '#fff', borderRadius: 8, padding: 24,
    ...Platform.select({
      web: { overflow: 'visible', zIndex: 2 } as any,
      ios: { zIndex: 2, shadowColor: 'rgba(0,94,192,1)', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.08, shadowRadius: 24 },
      android: { elevation: 10 },
    }),
  },

  // .card-head-row — design: flexDirection row, justify space-between, gap 16, flex-wrap, margin-bottom 12
  cardHeadRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 16, flexWrap: 'wrap' },
  cardHeadCol: { flex: 1, minWidth: 200 },

  // .card-title — design: font-size 20px font-weight 700
  cardTitle: { fontSize: 20, fontWeight: '700', color: '#2D2D2D', fontFamily: FontFamily.bold, lineHeight: 28, marginBottom: 4 },
  // .card-sub — design: font-size 13px color var(--nav-text-secondary)
  cardSub: { fontSize: 13, color: '#616161', fontFamily: FontFamily.regular },

  // .switcher — design: height 34, bg #EFF5FC, border-radius 8, padding 3, gap 2
  switcher: {
    flexDirection: 'row', alignSelf: 'flex-start',
    height: 34, backgroundColor: '#EFF5FC', borderRadius: 8, padding: 3, gap: 2,
  },
  // .sw-opt — design: padding 4px 16px; font-size 14px font-weight 500; height 28; border-radius 6
  swOpt: { paddingHorizontal: 16, paddingVertical: 4, borderRadius: 6, height: 28, alignItems: 'center', justifyContent: 'center' },
  swOptActive: { backgroundColor: '#fff',
    ...Platform.select({
      ios: { shadowColor: 'rgba(0,94,192,1)', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.08, shadowRadius: 24 },
      android: { elevation: 1 },
    }),
  },
  swOptText: { fontSize: 14, fontWeight: '500', color: '#616161', fontFamily: FontFamily.medium },
  swOptTextActive: { color: '#034895', fontWeight: '700', fontFamily: FontFamily.bold },

  // .sscc-row — design: display flex, flex-wrap, gap 12
  // zIndex here propagates the stacking context from the card down to routeSection
  ssccRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, marginTop: 8, flexWrap: 'wrap', zIndex: 100 },
  // .sscc-box — design: flex 1, min-width 240, max-width 430
  ssccBox: { flex: 1, minWidth: 240, maxWidth: 430 },
  ssccWrap: { position: 'relative', flexDirection: 'row', alignItems: 'center' },

  // .field label — design: font-size 14px font-weight 700 line-height 20px
  fieldLabel: { fontSize: 14, fontWeight: '700', color: '#2D2D2D', fontFamily: FontFamily.bold, lineHeight: 20, marginBottom: 8 },

  // .tin — design: height 40, padding 4px 12px, border 1px solid #D0D0D0, border-radius 6, font-size 14
  textInput: {
    flex: 1, height: 40, paddingHorizontal: 12, paddingRight: 48,
    borderWidth: 1, borderColor: '#D0D0D0', borderRadius: 6,
    fontSize: 14, color: '#2D2D2D', backgroundColor: '#fff', fontFamily: FontFamily.regular,
  },
  textInputError: { borderColor: '#C12C01' },

  // .go-arrow — design: position absolute right 5, width 32, height 32, border-radius 50%
  goArrow: { position: 'absolute', right: 5, width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center', backgroundColor: '#E6E6E6', cursor: 'not-allowed' as any },
  goArrowReady: { backgroundColor: '#027C06', cursor: 'pointer' as any },

  // .sub-meta — design: flex row, justify-content space-between, align-items center, margin-top 6
  subMeta: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 },
  errorText: { fontSize: 12, color: '#C12C01', fontWeight: '500', fontFamily: FontFamily.medium, flex: 1 },
  scanHint: { fontSize: 12, color: '#838383', fontFamily: FontFamily.regular },
  counter: { fontSize: 12, color: '#838383', fontWeight: '500', fontFamily: FontFamily.medium },
  counterFull: { color: '#027C06', fontWeight: '700' },

  // Scan button column — marginTop aligns button with the input (fieldLabel lineHeight 20 + marginBottom 8 = 28)
  scanSection: { flexDirection: 'column', gap: 6, alignItems: 'flex-start', marginTop: 28 },
  // .btn.btn-scan — design: bg #fff, border 1px solid nav-blue-800 #034895, border-radius 24, height 40, font 16 bold
  scanBtn: {
    height: 40, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, paddingHorizontal: 24, borderWidth: 1, borderColor: '#034895', borderRadius: 24, backgroundColor: '#fff',
  },
  scanBtnDisabled: { borderColor: '#D0D0D0', backgroundColor: '#F5F9FF' },
  scanBtnText: { fontSize: 16, fontWeight: '700', color: '#034895', fontFamily: FontFamily.bold },
  scanBtnTextDisabled: { color: '#838383', fontWeight: '500' },
  scanHelpText: { fontSize: 12, color: '#838383', fontFamily: FontFamily.regular },

  // Customer typeahead — zIndex propagates the cardStopIssue stacking context
  // down so the dropdown floats above all sibling cards below.
  taWrap: { position: 'relative', zIndex: 999, overflow: 'visible' as any },
  taDropdown: {
    position: 'absolute', top: 44, left: 0, right: 0, zIndex: 9999,
    backgroundColor: '#fff', borderWidth: 1, borderColor: '#D0D0D0',
    borderRadius: 8, overflow: 'hidden',
    ...Platform.select({ ios: { shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.1, shadowRadius: 8 }, android: { elevation: 4 } }),
  },
  // Scrollable list inside the dropdown — capped height makes it vertically scrollable
  taList: { maxHeight: 228 },
  taOption: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 9, borderBottomWidth: 1, borderBottomColor: '#EBEBEB' },
  taCof: { fontSize: 14, fontWeight: '700', color: '#034895', fontFamily: FontFamily.bold },
  taCofCol: {
    width: 92,
    fontSize: 14,
    fontWeight: '700',
    color: '#034895',
    fontFamily: FontFamily.bold,
    textAlign: 'right',
  },
  taSep: {
    width: 14,
    textAlign: 'center',
    fontSize: 14,
    color: '#2D2D2D',
    fontFamily: FontFamily.regular,
  },
  taName: { fontSize: 14, color: '#2D2D2D', fontFamily: FontFamily.regular, flex: 1 },
  taEmpty: { padding: 12, fontSize: 13, color: '#838383', fontFamily: FontFamily.regular },

  // Route Issue - HarborDropdown wrapper — zIndex lets the open menu float above the table below
  routeSection: { flex: 1, minWidth: 240, maxWidth: 360, zIndex: 100 },
  // Override dropdown's built-in marginBottom so it sits flush in the ssccRow
  routeDropdownStyle: { marginBottom: 0, width: '100%' },

  // Begin button — design: .btn.btn-primary height 40 border-radius 24 font-size 16 font-weight 700
  beginBtn: { height: 40, paddingHorizontal: 24, borderRadius: 24, backgroundColor: '#034895', alignItems: 'center', justifyContent: 'center', alignSelf: 'flex-end' },
  beginBtnDisabled: { backgroundColor: '#E6E6E6' },
  beginBtnText: { fontSize: 16, fontWeight: '700', color: '#fff', fontFamily: FontFamily.bold },
  beginBtnTextDisabled: { color: '#838383' },

  // Table card — design: .tbl-card bg white border-radius 8 shadow elev-01, overflow hidden
  tableCard: {
    backgroundColor: '#fff', borderRadius: 8, overflow: 'hidden',
    ...Platform.select({
      ios: { shadowColor: 'rgba(0,94,192,1)', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.08, shadowRadius: 24 },
      android: { elevation: 2 },
    }),
  },
  // .tbl-top — design: flex row, justify space-between, padding 16px 20px 12px, flex-wrap, gap 14
  tableTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingTop: 16, paddingBottom: 12, flexWrap: 'wrap', gap: 14 },
  // .tbl-summary — design: font-size 13px font-weight 600 color text-secondary
  tableSummary: { fontSize: 13, fontWeight: '600', color: '#616161', fontFamily: FontFamily.medium },
  tableTools: { flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  // .search-wrap — design: height 34, border 1px #D0D0D0, border-radius 6, min-width 230
  searchWrap: { flexDirection: 'row', alignItems: 'center', gap: 8, height: 34, paddingHorizontal: 12, backgroundColor: '#fff', borderWidth: 1, borderColor: '#D0D0D0', borderRadius: 6, minWidth: 230 },
  searchInput: { flex: 1, fontSize: 14, color: '#2D2D2D', fontFamily: FontFamily.regular },
  // .btn-ghost clear link
  clearFiltersBtn: { fontSize: 14, fontWeight: '700', color: '#005CBC', fontFamily: FontFamily.bold, paddingHorizontal: 10, paddingVertical: 6 },

  // Quick filter pills — design: .qpills / .qpill
  pillsScroll: { maxHeight: 52 },
  pillsContainer: { flexDirection: 'row', gap: 8, paddingHorizontal: 16, paddingVertical: 16 },
  // .qpill — design: height 32, padding 0 14, border-radius 999, border 1.5px #E6E6E6, bg #fff, font-size 13 font-weight 600
  pill: { flexDirection: 'row', alignItems: 'center', gap: 7, height: 32, paddingHorizontal: 14, borderRadius: 999, borderWidth: 1.5, borderColor: '#E6E6E6', backgroundColor: '#fff' },
  pillActive: { backgroundColor: '#005CBC', borderColor: '#005CBC' },
  pillText: { fontSize: 13, fontWeight: '600', color: '#616161', fontFamily: FontFamily.medium },
  pillTextActive: { color: '#fff' },
  // .qp-n — design: font-size 11, font-weight 700, bg nav-bg-selected, color blue-800, border-radius 999, padding 1px 7px
  pillCount: { backgroundColor: '#EFF5FC', borderRadius: 999, paddingHorizontal: 7, paddingVertical: 1 },
  pillCountActive: { backgroundColor: 'rgba(255,255,255,0.22)' },
  pillCountText: { fontSize: 11, fontWeight: '700', color: '#034895', fontFamily: FontFamily.bold },
  pillCountTextActive: { color: '#fff' },

  // Mobile cards
  cardList: { padding: 16, gap: 12 },
  mobileCard: { borderWidth: 1, borderColor: '#EBEBEB', borderRadius: 10, padding: 14, gap: 8, backgroundColor: '#fff' },
  mobileCardTop: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 },
  mobileDate: { fontSize: 13, color: '#838383', fontFamily: FontFamily.regular },
  mobileRow: { flexDirection: 'row', gap: 8 },
  mobileLabel: { fontSize: 12, fontWeight: '700', color: '#838383', fontFamily: FontFamily.bold, textTransform: 'uppercase', letterSpacing: 0.3, width: 56 },
  mobileValue: { fontSize: 13, color: '#2D2D2D', fontFamily: FontFamily.regular, flex: 1 },

  // Empty state
  emptyState: { padding: 46, alignItems: 'center', gap: 14 },
  emptyText: { fontSize: 14, color: '#616161', fontFamily: FontFamily.regular, textAlign: 'center' },
  reportRouteIssueBtn: {
    height: 40,
    paddingHorizontal: 24,
    borderRadius: 24,
    backgroundColor: '#005CBC',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 190,
  },
  reportRouteIssueBtnText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#fff',
    fontFamily: FontFamily.bold,
  },

  // Pagination footer
  pagFoot: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 4, padding: 12 },
  pgBtn: { minWidth: 30, height: 30, borderRadius: 6, borderWidth: 1, borderColor: '#EBEBEB', backgroundColor: '#fff', alignItems: 'center', justifyContent: 'center', paddingHorizontal: 8 },
  pgBtnActive: { backgroundColor: '#034895', borderColor: '#034895' },
  pgBtnDisabled: { opacity: 0.4 },
  pgBtnText: { fontSize: 13, fontWeight: '600', color: '#616161', fontFamily: FontFamily.medium },
  pgBtnActiveText: { color: '#fff' },

  // Modal — design: .overlay / .modal
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modalBox: { backgroundColor: '#fff', borderRadius: 8, width: '100%', maxWidth: 430, overflow: 'hidden',
    ...Platform.select({ ios: { shadowColor: '#000', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.18, shadowRadius: 24 }, android: { elevation: 8 } }),
  },
  modalHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 24, paddingVertical: 16, borderBottomWidth: 1, borderBottomColor: '#EBEBEB' },
  modalTitle: { fontSize: 20, fontWeight: '700', color: '#2D2D2D', fontFamily: FontFamily.bold },
  modalBody: { padding: 24, gap: 12 },
  // .warn-banner — design: bg nav-yellow-50 #FEF1B9, border nav-yellow-200 #E5A408, border-radius 8, font-size 14, color #6b4a05
  warnBanner: { flexDirection: 'row', gap: 10, alignItems: 'flex-start', backgroundColor: '#FEF1B9', borderWidth: 1, borderColor: '#E5A408', borderRadius: 8, padding: 12, paddingHorizontal: 14 },
  warnBannerText: { fontSize: 14, lineHeight: 20, color: '#6b4a05', fontFamily: FontFamily.regular, flex: 1 },
  fine: { fontSize: 12, color: '#838383', fontFamily: FontFamily.regular, lineHeight: 18 },
  // .modal-f — design: flex row, justify flex-end, gap 16, padding 16px 24px, border-top
  modalFoot: { flexDirection: 'row', justifyContent: 'flex-end', gap: 16, paddingHorizontal: 24, paddingVertical: 16, borderTopWidth: 1, borderTopColor: '#EBEBEB' },
  modalBtnSecondary: { height: 40, paddingHorizontal: 24, borderRadius: 24, borderWidth: 1, borderColor: '#034895', alignItems: 'center', justifyContent: 'center' },
  modalBtnSecondaryText: { fontSize: 16, fontWeight: '700', color: '#034895', fontFamily: FontFamily.bold },
  modalBtnPrimary: { height: 40, paddingHorizontal: 24, borderRadius: 24, backgroundColor: '#034895', alignItems: 'center', justifyContent: 'center' },
  modalBtnPrimaryText: { fontSize: 16, fontWeight: '700', color: '#fff', fontFamily: FontFamily.bold },
});
